import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useParams,
  useRouteContext,
  useRouterState,
} from "@tanstack/react-router";

import { type QueryClient } from "@tanstack/react-query";
import { initPromise } from "@/lib/i18n/init";
import { I18nProvider } from "@/components/I18nProvider";
import GeneralError from "@/components/general-error";
import { AlertRenderer } from "@/components/alert-renderer";
import { ConfirmDialogProvider } from "@/hooks/use-confirm-dialog";
import { ZipCodeDialogProvider } from "@/hooks/use-zipcode-dialog";
import { UpsellDialogProvider } from "@/components/products/upsell-dialog";

import { getServerHeaders } from "@/lib/i18n/actions";
import OpenReplayProvider from "@/providers/open-replay-provider";
import { NoScriptFallback } from "@/components/no-script-fallback";

interface MyRouterContext {
  queryClient: QueryClient;
  origin?: string;
  /** Scheme + host of the current request, e.g. `https://bestellen.example.nl`. */
  baseUrl?: string;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  errorComponent: GeneralError,
  beforeLoad: async () => {
    let origin: string | undefined;
    let baseUrl: string | undefined;
    try {
      const headers = await getServerHeaders();
      origin = headers["x-forwarded-host"] ?? headers["X-Forwarded-Host"] ?? undefined;
      // `x-forwarded-host` only exists behind a proxy, so fall back to `host`
      // for the absolute URL shown by the no-JavaScript fallback. Kept separate
      // from `origin` so the blocked-domain check below is unaffected.
      const host = origin ?? headers["host"] ?? headers["Host"];
      const protocol =
        headers["x-forwarded-proto"] ?? headers["X-Forwarded-Proto"] ?? "https";
      baseUrl = host ? `${protocol}://${host}` : undefined;
    } catch {
      // getServerHeaders may fail if request context is unavailable
    }

    if (origin?.includes("vrodex-orders-") || origin?.includes("orders-api")) {
      return { origin };
    }
    await initPromise;
    return { origin, baseUrl };
  },
  shellComponent: RootComponent,
});

function RootComponent() {


  const origin = useRouteContext({ strict: false }).origin;
  if (origin?.includes("vrodex-orders-") || origin?.includes("orders-api")) {
    return <div>Not allowed</div>;
  }
  return (

    <RootDocument>
      <OpenReplayProvider>
        <I18nProvider>
          <Outlet />
          <AlertRenderer />
          <ConfirmDialogProvider />
          <ZipCodeDialogProvider />
          <UpsellDialogProvider />
        </I18nProvider>
      </OpenReplayProvider>
    </RootDocument>
  );
}

function RootDocument({ children }: { children: React.ReactNode }) {
  // Get the current locale from the route params
  const params = useParams({ strict: false });
  const locale = (params as any)?.locale || "en";

  const baseUrl = useRouteContext({ strict: false }).baseUrl;
  const href = useRouterState({ select: (state) => state.location.href });
  const url = baseUrl ? `${baseUrl}${href}` : href;

  return (
    <html lang={locale}>
      <head>
        <HeadContent />
      </head>
      <body className="h-dvh flex flex-col bg-[#fdfdfb]">
        <NoScriptFallback locale={locale} url={url} />
        {children}
        <Scripts />
      </body>
    </html>
  );
}
